import React, { useState, useEffect, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PushNotificationService from '../lib/pushNotificationService';

interface NotificationData {
  id: string;
  title: string;
  message: string;
  type: 'message' | 'mention' | 'like' | 'reply' | 'system';
  senderName?: string;
  senderAvatar?: string;
  timestamp: Date;
  data?: any;
}

interface GlobalNotificationBarProps {
  onNotificationClick?: (notification: NotificationData) => void;
}

export const GlobalNotificationBar: React.FC<GlobalNotificationBarProps> = ({
  onNotificationClick
}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationRef = useRef<HTMLDivElement>(null);

  // Handle notification click
  const handleNotificationClick = (notification: NotificationData) => {
    // Hide the notification
    setIsVisible(false);
    
    // Call the callback if provided
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    
    // Navigate to chat if it's a message notification
    if (notification.type === 'message' && notification.data?.sender_id) {
      // Navigate to chat with the sender
      window.location.href = `/chat?sender=${notification.data.sender_id}`;
    }
  };

  // Show notification with animation
  const showNotification = (notification: NotificationData) => {
    setCurrentNotification(notification);
    setIsVisible(true);
    
    // Auto-hide after 5 seconds
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 5000);
  };

  // Handle manual close
  const handleClose = () => {
    setIsVisible(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Initialize push notification service
  useEffect(() => {
    if (user) {
      PushNotificationService.getInstance().initialize();
    }
  }, [user]);

  // Fetch sender name
  const fetchSenderName = async (senderId: string): Promise<string> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, username')
        .eq('id', senderId)
        .single();
      
      if (error) {
        console.error('Error fetching sender name:', error);
        return 'Someone';
      }
      
      return data?.full_name || data?.username || 'Someone';
    } catch (error) {
      console.error('Error fetching sender name:', error);
      return 'Someone';
    }
  };

  // Listen for new messages
  useEffect(() => {
    if (!user) return;

    console.log("🔔 Setting up global notification listener...");

    const channel = supabase
      .channel(`global_notifications_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const newMessage = payload.new as any;
          
          // Only show notification if we're the receiver and not in the chat
          if (newMessage.receiver_id === user.id) {
            console.log("🔔 New message notification received:", newMessage);
            
            // Check if we're currently in the chat with this sender
            const currentPath = window.location.pathname;
            const isInChat = currentPath.startsWith('/chat');
            
            if (!isInChat) {
              // Fetch sender name
              const senderName = await fetchSenderName(newMessage.sender_id);
              
              // Show iOS-style notification bar if app is visible
              if (!document.hidden) {
                const notification: NotificationData = {
                  id: newMessage.id,
                  title: 'New Message',
                  message: newMessage.content,
                  type: 'message',
                  senderName: senderName,
                  timestamp: new Date(),
                  data: {
                    sender_id: newMessage.sender_id,
                    message_id: newMessage.id
                  }
                };
                
                showNotification(notification);
              }
              
              // Show push notification if app is not focused
              if (document.hidden) {
                const pushService = PushNotificationService.getInstance();
                pushService.showMessageNotification(senderName, newMessage.content, newMessage.sender_id);
              }
            }
          }
        }
      )
      .subscribe((status) => {
        console.log("🔔 Global notification subscription status:", status);
      });

    return () => {
      console.log("🔔 Cleaning up global notification listener");
      supabase.removeChannel(channel);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!isVisible || !currentNotification) {
    return null;
  }

  return (
    <div
      ref={animationRef}
      className="fixed top-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-out"
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)'
      }}
    >
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="max-w-md mx-auto">
          <div 
            className="flex items-center p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => handleNotificationClick(currentNotification)}
          >
            {/* Avatar */}
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
              {currentNotification.senderName?.charAt(0) || 'U'}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {currentNotification.title}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <IoClose className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-1">
                {currentNotification.message}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {currentNotification.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
